import React, { useState, useRef, useEffect } from 'react';
import { X, Send, RefreshCw, ShoppingCart } from 'lucide-react';
import { aiChat } from '@recipe/shared';
import './AIChatModal.css';

const QUICK_ACTIONS = [
  { icon: '🛒', label: '加食材', prompt: '我想做{dish}，帮我列出需要的食材和用量，并添加到购物车', placeholder: '输入菜名，如：红烧肉' },
  { icon: '🔄', label: '替换', prompt: '我没有{item}，有什么食材可以替代？请给出替代品和用量建议', placeholder: '输入要替换的食材' },
  { icon: '🍳', label: '做什么菜', prompt: '我现在有这些食材：{items}。请推荐3道能做的菜，简单说明做法', useCart: true },
  { icon: '📅', label: '周菜单', prompt: '帮我规划一周的中餐菜单（每天午餐和晚餐），并列出所有需要采购的食材，添加到购物车', direct: true },
  { icon: '📊', label: '营养检查', prompt: '分析这些食材的营养均衡性：{items}。指出缺乏的营养素并建议补充的食材', useCart: true },
  { icon: '🔢', label: '用量估算', prompt: '我有{count}个人吃饭，帮我估算这些食材各需要多少：{items}', useCart: true, placeholder: '输入人数，如：3' },
];

const AIChatModal = ({ isOpen, onClose, cartItems = [], onAddItems }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'model',
        text: '你好！我是小厨 🧑‍🍳\n\n我可以帮你：\n• 列出做一道菜需要的食材\n• 推荐食材替代品\n• 根据已有食材推荐菜谱\n• 规划一周菜单\n• 检查营养均衡\n• 估算食材用量\n\n选择下方快捷操作或直接问我~',
      }]);
    }
  }, [isOpen, messages.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const getCartItemNames = () => {
    return cartItems
      .filter((i) => !i.checked)
      .map((i) => i.name)
      .join('、') || '(购物清单为空)';
  };

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', text: text.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setSelectedAction(null);
    setLoading(true);

    try {
      const chatHistory = newMessages.slice(-10).map((m) => ({
        role: m.role,
        text: m.text,
      }));
      const result = await aiChat(chatHistory);
      setMessages((prev) => [...prev, { role: 'model', text: result.text, items: result.items }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'model', text: `抱歉，出了点问题：${e.message}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    if (action.direct) {
      sendMessage(action.prompt);
      return;
    }
    if (action.useCart && !action.placeholder) {
      const itemsStr = getCartItemNames();
      const prompt = action.prompt.replace('{items}', itemsStr);
      sendMessage(prompt);
      return;
    }
    setSelectedAction(action);
  };

  const handleQuickActionSubmit = () => {
    if (!selectedAction || !input.trim()) return;
    let prompt = selectedAction.prompt;

    if (selectedAction.useCart) {
      const itemsStr = getCartItemNames();
      prompt = prompt.replace('{items}', itemsStr).replace('{count}', input.trim());
    } else {
      prompt = prompt
        .replace('{dish}', input.trim())
        .replace('{item}', input.trim());
    }
    sendMessage(prompt);
  };

  const handleAddToCart = (items) => {
    if (onAddItems && items?.length > 0) {
      onAddItems(items);
      setMessages((prev) => [
        ...prev,
        { role: 'model', text: `✅ 已添加 ${items.length} 项到购物清单` },
      ]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (selectedAction) {
        handleQuickActionSubmit();
      } else {
        sendMessage(input);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ai-chat-overlay" onClick={onClose}>
      <div className="ai-chat-modal" onClick={e => e.stopPropagation()}>
        <div className="ai-chat-header">
          <h3>小厨 AI 助手</h3>
          <div className="ai-chat-header-actions">
            <button 
              className="icon-btn" 
              onClick={() => { setMessages([]); setSelectedAction(null); setInput(''); }}
              title="重置对话"
            >
              <RefreshCw size={18} />
            </button>
            <button className="icon-btn close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="ai-chat-messages">
          {messages.map((msg, index) => (
            <div key={index} className={`message-row ${msg.role === 'user' ? 'user' : 'model'}`}>
              {msg.role === 'model' && (
                <div className="avatar ai-avatar">🧑‍🍳</div>
              )}
              <div className={`message-bubble ${msg.error ? 'error' : ''}`}>
                <div className="message-text" style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                {msg.items && msg.items.length > 0 && (
                  <button 
                    className="add-items-btn"
                    onClick={() => handleAddToCart(msg.items)}
                  >
                    <ShoppingCart size={16} />
                    添加 {msg.items.length} 项到购物车
                  </button>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="message-row model">
              <div className="avatar ai-avatar">🧑‍🍳</div>
              <div className="message-bubble">
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {!selectedAction && (
          <div className="quick-actions-scroll">
            {QUICK_ACTIONS.map((action, i) => (
              <button
                key={i}
                className="quick-action-chip"
                onClick={() => handleQuickAction(action)}
              >
                <span className="icon">{action.icon}</span>
                <span className="label">{action.label}</span>
              </button>
            ))}
          </div>
        )}

        <div className="ai-chat-input-area">
          {selectedAction && (
            <div className="action-hint">
              <span className="hint-text">
                {selectedAction.icon} {selectedAction.label}
              </span>
              <button onClick={() => { setSelectedAction(null); setInput(''); }}>
                <X size={16} />
              </button>
            </div>
          )}
          <div className="input-row">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={selectedAction?.placeholder || '问小厨任何问题...'}
              className="chat-input"
            />
            <button 
              className="send-btn"
              disabled={!input.trim() && !selectedAction?.direct}
              onClick={() => {
                if (selectedAction) {
                  handleQuickActionSubmit();
                } else {
                  sendMessage(input);
                }
              }}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatModal;
